/**
 * Thin wrappers around the User Timing API for instrumenting the keystroke →
 * parse → render → commit boundary. Gated on Vite's `DEV` so production builds
 * are a true no-op (the helpers compile to dead code that tree-shakes away).
 *
 * Usage:
 *   import { mark, measure } from '@slate/ui/perf/marks';
 *   mark('render:start');
 *   ...work...
 *   const ms = measure('render', 'render:start');
 */

const ENABLED: boolean = (() => {
  try {
    return (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true
      && typeof performance !== 'undefined'
      && typeof performance.mark === 'function';
  } catch {
    return false;
  }
})();

export function mark(name: string): void {
  if (!ENABLED) return;
  try { performance.mark(name); } catch { /* noop */ }
}

/**
 * Create a measure between two marks (or `start` → now if `end` omitted), and
 * return its duration in ms. Returns `undefined` when disabled or on error.
 */
export function measure(name: string, start: string, end?: string): number | undefined {
  if (!ENABLED) return undefined;
  try {
    const m = performance.measure(name, start, end);
    return m.duration;
  } catch {
    return undefined;
  }
}

export function clear(...names: string[]): void {
  if (!ENABLED) return;
  try {
    for (const n of names) {
      performance.clearMarks(n);
      performance.clearMeasures(n);
    }
  } catch { /* noop */ }
}

/** True when perf marking is active (Vite DEV + Performance API available). */
export const perfEnabled = ENABLED;
